import { useCallback, useEffect, useMemo, useState } from "react";

import { useGetDealRoundInvestorsQuery } from "queries/query-selectors/tagger-query-selectors";
import { useTaggerActions, useTaggerDocumentSelector } from "store";

import { DocumentDataField } from "../../constants";
import { IProcessedTag, TaggerDocument } from "../../store/types";

const useRounds = (docId: TaggerDocument["id"], tag?: IProcessedTag) => {
  const doc = useTaggerDocumentSelector(docId);
  const { changeDocumentData } = useTaggerActions();
  const { data, isLoading } = useGetDealRoundInvestorsQuery(doc.dealId);

  const rounds = data || [];

  const options = rounds.map((round) => ({
    label: round.round_name,
    value: round.round_id
  }));

  const defaultValue = useMemo(() => {
    if (rounds.length === 1) return rounds[0].round_id;
    if (tag?.round?.id) return tag.round.id;
    return null;
  }, [rounds, tag, doc.data?.selectedRound]);

  const [value, setValue] = useState<number | null>(defaultValue);

  const handleChange = useCallback((value: number | null) => {
    setValue(value);
    changeDocumentData(docId, DocumentDataField.SelectedRound, value);
  }, []);

  useEffect(() => {
    setValue(tag?.round?.id || doc.data?.selectedRound || null);
  }, [tag]);

  useEffect(() => {
    // In case of a single round the user don't see a round select, so a default must be set.
    if (rounds.length === 1 && !doc.data?.selectedRound) {
      handleChange(rounds[0].round_id);
    }
  }, [rounds]);

  return {
    options: options,
    value: value,
    loading: isLoading,
    onChange: handleChange
  };
};

export default useRounds;
